import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION = 'budgets';

/**
 * Budget document ID format: 'yyyy-MM'
 * Document structure:
 * {
 *   month: 'yyyy-MM',
 *   totalBudget: number,
 *   categories: { [categoryId]: number },
 *   updatedAt: Timestamp
 * }
 */

/** Get budget for a given month */
export async function getBudget(month) {
  const snap = await getDoc(doc(db, COLLECTION, month));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/** Save (upsert) budget for a month */
export async function saveBudget(month, { totalBudget, categories }) {
  await setDoc(
    doc(db, COLLECTION, month),
    {
      month,
      totalBudget:  Number(totalBudget),
      categories:   Object.fromEntries(
        Object.entries(categories).map(([k, v]) => [k, Number(v)])
      ),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Get budgets for the last N months (for analytics) */
export async function getBudgetHistory(months = []) {
  const results = {};
  await Promise.all(
    months.map(async (m) => {
      const b = await getBudget(m);
      if (b) results[m] = b;
    })
  );
  return results;
}

/**
 * Calculate per-category status given spent amounts and budget.
 * Returns: { [categoryId]: { budget, spent, remaining, percent, status } }
 */
export function computeCategoryStatus(budgetData, categoryTotals) {
  if (!budgetData) return {};
  const result = {};
  const cats = budgetData.categories ?? {};

  for (const [catId, budgetAmt] of Object.entries(cats)) {
    const spent     = categoryTotals[catId] ?? 0;
    const remaining = budgetAmt - spent;
    const percent   = budgetAmt > 0 ? (spent / budgetAmt) * 100 : 0;
    let status = 'ok';
    if (percent >= 100) status = 'danger';
    else if (percent >= 80) status = 'warning';

    result[catId] = { budget: budgetAmt, spent, remaining, percent, status };
  }
  return result;
}
